// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  ChartDataEndpointPath,
  Math,
  UsageAnalyticsContextTypes,
} from 'common/report/queries/app-api/usage-analytics-client-types';
import {usageAnalyticsClient} from 'common/report/queries/app-api/usage-analytics-client';
import {userDetailsClient} from 'common/report/queries/app-api/user-details-client';
import {userDetailsStore} from 'common/report/stores/user-details';
import {getProjectId} from 'common/report/util';
import {useStore} from 'common/util/component-helpers';
import {emitTrackEvent} from 'common/util/dom';
import {updateAvatar} from 'common/widgets/avatar-upload/util';
import {IronContextRegistry} from 'common/report/context-registry';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {promisifyModal} from 'common/components/modal/utils';

import {TrackingEvent} from './tracking';
// eslint-disable-next-line import/no-cycle
import {Template as template} from './template';
import {TabOptions, TeammateSidebarJsxProps} from './types';
import {consolidateMostViewedResponse} from './util';
import css from './index.inline.styl';

export class TeammateSidebar extends Component<
  TeammateSidebarJsxProps[`state`],
  TeammateSidebarJsxProps[`attrs`],
  unknown,
  unknown,
  IronContextRegistry
> {
  public static get attrsSchema(): AttrsSchema<TeammateSidebarJsxProps[`attrs`]> {
    return {
      name: `string`,
      id: {
        type: `number`,
        default: null,
      },
      email: `string`,
      'project-id': `number`,
    };
  }
  get config() {
    return {
      css,
      defaultState: {
        mostViewed: null,
        selectedTab: TabOptions.MostViewed,
        user: null,
      },
      useShadowDom: true,
      template,
      contexts: [`currentProject` as const],
      helpers: {
        clearBio: (ev: MouseEvent) => {
          ev.stopPropagation();
          this.track(TrackingEvent.BioClear);
          userDetailsStore.patch({user_bio: null});
        },
        getProjectId: () => this.attr(`project-id`) || getProjectId(),
        handleAvatarChanged: ({detail}: CustomEvent) => this.updateAvatar(detail.avatar),
        handleModalChange: (ev: CustomEvent) => {
          if (ev.detail.state === `closed`) {
            this.track(TrackingEvent.Closed);
          }
        },
        handleTabSelect: (selectedTab: string) => {
          this.track(TrackingEvent.TabSelected, {selectedTab});
          this.update({selectedTab: selectedTab as TabOptions});
        },
        handleTextEditorSave: (ev: CustomEvent) => {
          const bio = ev.detail.value?.trim();
          this.track(TrackingEvent.BioSave, {bioLength: bio?.length});
          userDetailsStore.patch({user_bio: ev.detail.value?.trim()});
        },
        userIsMe: () => userDetailsStore.value?.email === this.attr(`email`),
        shouldShowDashboardLink: () => /mixpanel\.(com|org)/.exec(window.origin),
        trackRecentlyCreated: (props) => this.track(TrackingEvent.LinkClicked, {tab: TabOptions.MostViewed, ...props}),
        trackMostViewed: (props) => this.track(TrackingEvent.LinkClicked, {tab: TabOptions.RecentlyCreated, ...props}),
      },
    };
  }

  getCurrentProject(): CurrentProject | undefined {
    return this.getContext(`currentProject`).get().value;
  }

  async syncUserData() {
    const user = await userDetailsClient.query({projectId: this.helpers.getProjectId(), email: this.attr(`email`)});
    this.track(TrackingEvent.UserDataLoaded, {
      recentlyCreatedCount: user[`recently_created`]?.length,
    });
    this.update({user});
  }

  async syncUsageData() {
    const usageAnalyticsParams = {
      usageContext: UsageAnalyticsContextTypes.Project,
      id: this.attr(`project-id`),
      distinctId: this.attr(`id`).toString(),
      math: Math.Total,
      queryLimit: 10,
    };
    const [mostViewedReports, mostViewedDashboards] = await Promise.all([
      usageAnalyticsClient.getChartData({
        ...usageAnalyticsParams,
        chartEndpoint: ChartDataEndpointPath.MostPopularReports,
      }),
      usageAnalyticsClient.getChartData({
        ...usageAnalyticsParams,
        chartEndpoint: ChartDataEndpointPath.MostPopularDashboards,
      }),
    ]);
    this.update({
      mostViewed: consolidateMostViewedResponse(mostViewedReports, mostViewedDashboards),
    });
    this.track(TrackingEvent.MostViewedLoaded, {
      mostViewedCount: this.state.mostViewed.filter((entity) => !entity.isHidden).length,
    });
  }

  async updateAvatar(avatarPath: string) {
    if (await updateAvatar(avatarPath)) {
      this.track(TrackingEvent.AvatarUpdated);
    }
  }

  track(eventName, props = {}) {
    emitTrackEvent(this, {
      eventName,
      props: {
        email: this.attr(`email`),
        id: this.attr(`id`),
        ...props,
      },
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.track(TrackingEvent.Opened);
    useStore(this, userDetailsStore).fetch();
    this.syncUserData();
    this.syncUsageData();
  }
}

export const TeammateSidebarEl = defineAndMakeJsxFactory<TeammateSidebarJsxProps>(
  `mp-teammate-sidebar`,
  TeammateSidebar,
);
export const TeammateSidebarReact = panel2react<TeammateSidebarJsxProps>(`mp-teammate-sidebar`, TeammateSidebar);

export async function openTeammateSidebar(attrs: TeammateSidebarJsxProps[`attrs`]) {
  return promisifyModal({
    panelElement: TeammateSidebarEl,
    attrs,
    props: {},
    closeEvents: [`submit`],
  });
}
